<template>
  <form @submit.prevent="handleSubmit" class="beneficiary-form">
    <div class="form-field">
      <IntercomInput
        v-model="formData.name"
        :label="$t('beneficiary.name')"
        placeholder="Enter beneficiary name"
        required
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomPhoneInput
        v-model="formData.phone"
        :label="$t('beneficiary.phone')"
        :disabled="loading"
        @update:valid="(v) => (phoneValid = v)"
      />
    </div>

    <div class="form-field">
      <IntercomEmailInput
        v-model="formData.email"
        :label="$t('beneficiary.email')"
        placeholder="Enter email address"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomSelect
        v-model="formData.gender"
        :label="$t('beneficiary.gender')"
        :options="genderOptions"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomDateInput
        v-model="formData.date_of_birth"
        :label="$t('beneficiary.dateOfBirth')"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomSelect
        v-model="formData.beneficiary_type"
        :label="$t('beneficiary.type')"
        :options="typeOptions"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomSelect
        v-model="formData.category"
        :label="$t('beneficiary.category')"
        :options="categoryOptions"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.address"
        :label="$t('beneficiary.address')"
        placeholder="Enter address"
        :disabled="loading"
      />
    </div>

    <div class="form-field">
      <IntercomInput
        v-model="formData.notes"
        :label="$t('beneficiary.notes')"
        placeholder="Optional notes"
        :disabled="loading"
      />
    </div>

    <!-- KYC Documents Section -->
    <fieldset class="kyc-section">
      <legend class="kyc-legend">{{ $t('beneficiary.kycDocuments') }}</legend>

      <div v-for="(doc, index) in formData.kyc_documents" :key="index" class="kyc-row">
        <IntercomSelect
          v-model="doc.type"
          :label="index === 0 ? $t('beneficiary.idProofType') : ''"
          :options="idProofTypeOptions"
          :disabled="loading"
          class="kyc-type"
        />
        <IntercomInput
          v-model="doc.number"
          :label="index === 0 ? $t('beneficiary.idProofNumber') : ''"
          :placeholder="getIdProofPlaceholder(doc.type)"
          :disabled="loading"
          class="kyc-number"
        />
        <IntercomButton
          type="button"
          variant="ghost"
          size="sm"
          :disabled="loading"
          class="kyc-remove"
          @click="removeKYCDocument(index)"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </IntercomButton>
      </div>

      <IntercomButton
        type="button"
        variant="ghost"
        size="sm"
        :disabled="loading"
        @click="addKYCDocument"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </svg>
        {{ $t('beneficiary.addIdProof') }}
      </IntercomButton>
    </fieldset>

    <div class="form-actions">
      <IntercomButton type="button" variant="ghost" @click="$emit('cancel')" :disabled="loading">
        {{ $t('common.cancel') }}
      </IntercomButton>
      <IntercomButton type="submit" variant="primary" :loading="loading" :disabled="formData.phone && !phoneValid">
        {{ $t('common.save') }}
      </IntercomButton>
    </div>
  </form>
</template>

<script setup>
import { ref, reactive, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  IntercomInput,
  IntercomDateInput,
  IntercomPhoneInput,
  IntercomEmailInput,
  IntercomSelect,
  IntercomButton,
} from '@shared/components/intercom'

const { t } = useI18n()

const props = defineProps({
  beneficiary: { type: Object, default: null },
  loading: { type: Boolean, default: false },
})

const emit = defineEmits(['submit', 'cancel'])
const phoneValid = ref(false)

const formData = reactive({
  name: '',
  phone: '',
  email: '',
  gender: '',
  date_of_birth: '',
  beneficiary_type: 'individual',
  category: '',
  address: '',
  notes: '',
  kyc_documents: [],
})

const genderOptions = computed(() => [
  { value: '', label: t('common.optional') },
  { value: 'male', label: t('beneficiary.genders.male') },
  { value: 'female', label: t('beneficiary.genders.female') },
  { value: 'other', label: t('beneficiary.genders.other') },
])

const typeOptions = computed(() => [
  { value: 'individual', label: t('beneficiary.types.individual') },
  { value: 'household', label: t('beneficiary.types.household') },
  { value: 'group', label: t('beneficiary.types.group') },
])

const categoryOptions = computed(() => [
  { value: '', label: t('common.optional') },
  { value: 'bpl', label: t('beneficiary.categories.bpl') },
  { value: 'senior_citizen', label: t('beneficiary.categories.senior_citizen') },
  { value: 'disabled', label: t('beneficiary.categories.disabled') },
  { value: 'student', label: t('beneficiary.categories.student') },
  { value: 'general', label: t('beneficiary.categories.general') },
  { value: 'other', label: t('beneficiary.categories.other') },
])

const idProofTypeOptions = computed(() => [
  { value: 'aadhaar', label: t('beneficiary.idProofTypes.aadhaar') },
  { value: 'ration_card', label: t('beneficiary.idProofTypes.ration_card') },
  { value: 'voter_id', label: t('beneficiary.idProofTypes.voter_id') },
  { value: 'pan', label: t('beneficiary.idProofTypes.pan') },
  { value: 'driving_license', label: t('beneficiary.idProofTypes.driving_license') },
  { value: 'other', label: t('beneficiary.idProofTypes.other') },
])

const getIdProofPlaceholder = (type) => {
  switch (type) {
    case 'aadhaar': return '12-digit Aadhaar number'
    case 'pan': return 'ABCDE1234F'
    case 'voter_id': return 'Voter ID number'
    case 'ration_card': return 'Ration card number'
    case 'driving_license': return 'DL number'
    default: return 'Document number'
  }
}

const addKYCDocument = () => {
  formData.kyc_documents.push({ type: 'aadhaar', number: '' })
}

const removeKYCDocument = (index) => {
  formData.kyc_documents.splice(index, 1)
}

watch(() => props.beneficiary, (newBeneficiary) => {
  if (newBeneficiary) {
    formData.name = newBeneficiary.name || ''
    formData.phone = newBeneficiary.phone || ''
    formData.email = newBeneficiary.email || ''
    formData.gender = newBeneficiary.gender || ''
    formData.date_of_birth = newBeneficiary.date_of_birth ? newBeneficiary.date_of_birth.slice(0, 10) : ''
    formData.beneficiary_type = newBeneficiary.beneficiary_type || 'individual'
    formData.category = newBeneficiary.category || ''
    formData.address = newBeneficiary.address || ''
    formData.notes = newBeneficiary.notes || ''
    formData.kyc_documents = newBeneficiary.kyc_documents?.length
      ? newBeneficiary.kyc_documents.map(d => ({ ...d }))
      : []
  }
}, { immediate: true })

const handleSubmit = () => {
  const data = { ...formData }
  if (!data.gender) delete data.gender
  if (!data.date_of_birth) delete data.date_of_birth
  if (!data.phone) data.phone = null
  if (!data.email) delete data.email
  if (!data.category) delete data.category
  // Filter out incomplete KYC docs
  data.kyc_documents = (data.kyc_documents || []).filter(d => d.type && d.number)
  emit('submit', data)
}
</script>

<style scoped>
.beneficiary-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
}

.form-field {
  display: flex;
  flex-direction: column;
}

.kyc-section {
  border: 1px solid var(--color-gray-200);
  border-radius: var(--radius-md);
  padding: var(--space-4);
  margin: 0;
}

.kyc-legend {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-gray-600);
  padding: 0 var(--space-2);
}

.kyc-row {
  display: flex;
  gap: var(--space-3);
  align-items: flex-end;
  margin-bottom: var(--space-3);
}

.kyc-type {
  flex: 1;
}

.kyc-number {
  flex: 1.5;
}

.kyc-remove {
  flex-shrink: 0;
  margin-bottom: var(--space-1);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-3);
  margin-top: var(--space-4);
  padding-top: var(--space-4);
  border-top: 1px solid var(--color-gray-200);
}
</style>
